import * as React from "react";
import { IconChevronUp, IconChevronDown, IconSelector } from "@tabler/icons-react";
import { Skeleton } from "./skeleton";
import { cn } from "../../lib/utils";

const DataTable = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border border-glass-border bg-surface-1 overflow-hidden",
      className
    )}
  >
    <div className="overflow-x-auto">
      <table className="w-full" {...props} />
    </div>
  </div>
));
DataTable.displayName = "DataTable";

const DataTableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("bg-surface-2 border-b border-glass-border", className)}
    {...props}
  />
));
DataTableHeader.displayName = "DataTableHeader";

const DataTableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
DataTableBody.displayName = "DataTableBody";

const DataTableRow = React.memo(
  React.forwardRef(
    ({ className, selected, ...props }, ref) => (
      <tr
        ref={ref}
        className={cn(
          "border-b border-glass-border last:border-b-0",
          "hover:bg-glass-bg-hover",
          selected && "bg-accent-muted/50",
          className
        )}
        {...props}
      />
    )
  )
);
DataTableRow.displayName = "DataTableRow";

const DataTableHead = React.forwardRef(
  ({ className, sortable, sorted, onSort, children, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-tertiary",
        sortable && "cursor-pointer select-none hover:text-text-secondary",
        className
      )}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortable && (
          <span className="flex-shrink-0">
            {sorted === "asc" ? (
              <IconChevronUp className="h-3.5 w-3.5 text-accent" />
            ) : sorted === "desc" ? (
              <IconChevronDown className="h-3.5 w-3.5 text-accent" />
            ) : (
              <IconSelector className="h-3.5 w-3.5 text-text-tertiary/50" />
            )}
          </span>
        )}
      </div>
    </th>
  )
);
DataTableHead.displayName = "DataTableHead";

const DataTableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("px-4 py-3 text-sm text-text-primary", className)}
    {...props}
  />
));
DataTableCell.displayName = "DataTableCell";

const DataTableEmpty = React.forwardRef(
  ({ className, colSpan, icon: Icon, title, description, action, ...props }, ref) => (
    <tr ref={ref}>
      <td colSpan={colSpan} className={cn(className)} {...props}>
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          {Icon && (
            <div className="w-12 h-12 bg-surface-3 flex items-center justify-center mb-4">
              <Icon className="h-6 w-6 text-text-tertiary" />
            </div>
          )}
          {title && (
            <p className="text-sm font-medium text-text-primary mb-1">
              {title}
            </p>
          )}
          {description && (
            <p className="text-sm text-text-tertiary max-w-sm mb-6">
              {description}
            </p>
          )}
          {action}
        </div>
      </td>
    </tr>
  )
);
DataTableEmpty.displayName = "DataTableEmpty";

const DataTableSkeleton = React.forwardRef(
  ({ className, columns = 4, rows = 5, ...props }, ref) => (
    <tbody ref={ref} className={cn(className)} {...props}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr
          key={rowIndex}
          className="border-b border-glass-border last:border-b-0"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-4 py-3">
              <Skeleton
                className="h-4"
                style={{ width: `${60 + Math.random() * 30}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
);
DataTableSkeleton.displayName = "DataTableSkeleton";

export {
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableHead,
  DataTableCell,
  DataTableEmpty,
  DataTableSkeleton,
};
